import type { FindingRecord, Run, Severity, Target } from './types';

/** Worst-first, matching Findings.tsx/badge.tsx's SEVERITY_ORDER everywhere else in the app. */
export const SEVERITY_ORDER: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

/** Counts OPEN findings by severity — same "what needs attention now" framing as the rest of the
 * dashboard (the existing Crit/High KPI card already filters to `status === 'open'`). */
export function severityBreakdown(findings: FindingRecord[]): Record<Severity, number> {
  const counts: Record<Severity, number> = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
  for (const f of findings) {
    if (f.status !== 'open') continue;
    counts[f.severity]++;
  }
  return counts;
}

export interface TrendPoint {
  /** ISO date (YYYY-MM-DD), UTC day bucket. */
  date: string;
  /** Short display label, e.g. "Jul 12". */
  label: string;
  count: number;
}

/**
 * Buckets findings by their `discoveredAt` calendar day (UTC) over the trailing `days` window
 * ending on `now`, INCLUDING zero-count days — a sparse trend line would misleadingly look like
 * data is missing rather than genuinely zero. `now` is a parameter (not `new Date()` internally)
 * purely so this stays a pure, deterministically-testable function.
 */
export function findingsTrend(findings: FindingRecord[], days: number, now: Date): TrendPoint[] {
  const dayMs = 24 * 60 * 60 * 1000;
  const endDay = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const startDay = endDay - (days - 1) * dayMs;

  const countByDay = new Map<number, number>();
  for (const f of findings) {
    const discovered = new Date(f.discoveredAt);
    const day = Date.UTC(discovered.getUTCFullYear(), discovered.getUTCMonth(), discovered.getUTCDate());
    if (day < startDay || day > endDay) continue;
    countByDay.set(day, (countByDay.get(day) ?? 0) + 1);
  }

  const points: TrendPoint[] = [];
  for (let day = startDay; day <= endDay; day += dayMs) {
    const d = new Date(day);
    points.push({
      date: d.toISOString().slice(0, 10),
      label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }),
      count: countByDay.get(day) ?? 0,
    });
  }
  return points;
}

export type PostureLabel = 'Good' | 'Fair' | 'Poor';

export interface SecurityPosture {
  score: number;
  label: PostureLabel;
  /** Semantic token name — the gauge/badge consuming this picks the matching Tailwind class. */
  tone: 'success' | 'warning' | 'danger';
}

/** Formula given by the design brief: 100 - (critical*15 + high*8 + medium*3 + low*1), floored at 0. */
export function computeSecurityPosture(counts: Pick<Record<Severity, number>, 'critical' | 'high' | 'medium' | 'low'>): SecurityPosture {
  const penalty = counts.critical * 15 + counts.high * 8 + counts.medium * 3 + counts.low * 1;
  const score = Math.max(0, 100 - penalty);
  if (score < 40) return { score, label: 'Poor', tone: 'danger' };
  if (score <= 70) return { score, label: 'Fair', tone: 'warning' };
  return { score, label: 'Good', tone: 'success' };
}

export interface AttackTarget {
  target: string;
  count: number;
  /** 0-100, same weighting shape as computeSecurityPosture but per-target and NOT inverted —
   * higher means riskier, capped at 100 rather than floored at 0. */
  riskScore: number;
  tone: 'danger' | 'warning' | 'info';
}

function riskTone(riskScore: number): AttackTarget['tone'] {
  if (riskScore >= 70) return 'danger';
  if (riskScore >= 40) return 'warning';
  return 'info';
}

/** Ranks targets by open-findings risk — same severity weights as computeSecurityPosture, scaled
 * up (not down from 100) since this is "how risky is this target," not "how healthy is the whole
 * tenant." Ties broken by raw open-finding count. */
export function topAttackTargets(findings: FindingRecord[], limit: number): AttackTarget[] {
  const bySeverityByTarget = new Map<string, Record<Severity, number>>();
  for (const f of findings) {
    if (f.status !== 'open') continue;
    const counts = bySeverityByTarget.get(f.target) ?? { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    counts[f.severity]++;
    bySeverityByTarget.set(f.target, counts);
  }

  const ranked: AttackTarget[] = [...bySeverityByTarget.entries()].map(([target, counts]) => {
    const weighted = counts.critical * 25 + counts.high * 15 + counts.medium * 5 + counts.low * 2 + counts.info * 1;
    const riskScore = Math.min(100, weighted);
    const count = counts.critical + counts.high + counts.medium + counts.low + counts.info;
    return { target, count, riskScore, tone: riskTone(riskScore) };
  });

  return ranked.sort((a, b) => b.riskScore - a.riskScore || b.count - a.count).slice(0, limit);
}

/** Real, honest KPI context lines — never a fabricated delta. Each one is a plain count over an
 * actual window of real records already in memory, not a guessed comparison against a snapshot
 * this app doesn't keep. */
export function targetsAddedSince(targets: Target[], since: Date): number {
  return targets.filter((t) => new Date(t.createdAt).getTime() >= since.getTime()).length;
}

export function runsStartedSince(runs: Run[], since: Date): number {
  return runs.filter((r) => new Date(r.createdAt).getTime() >= since.getTime()).length;
}

export function findingsDiscoveredSince(findings: FindingRecord[], since: Date, severities: Severity[]): number {
  return findings.filter((f) => severities.includes(f.severity) && new Date(f.discoveredAt).getTime() >= since.getTime()).length;
}

export function completedRunsSince(runs: Run[], since: Date): number {
  return runs.filter((r) => r.status === 'completed' && new Date(r.updatedAt).getTime() >= since.getTime()).length;
}
